/**
 * Un .xlsx DE VERDAD, armado en el navegador y sin librerías (0.42.0) — el espejo de `hoja.ts`, que los lee.
 *
 * Por qué no un CSV: según la configuración regional, Excel parte mal las columnas (coma o punto y coma), rompe los
 * acentos si no reconoce UTF-8 y se come los ceros o el "+" de un teléfono. Aquí cada celda lleva su tipo: texto
 * (teléfonos incluidos), número, dinero, fecha o fecha y hora (en la hora de la clínica). El encabezado va en negritas,
 * fijo al bajar y con filtro, y cada columna con su ancho.
 *
 * Un .xlsx es un ZIP de XMLs. Se guarda sin comprimir (método "store"): así no hace falta ninguna librería ni API del
 * navegador, y para unos miles de filas pesa poco. El texto va como `inlineStr` (sin tabla de textos compartidos):
 * un "=" al inicio de un nombre NO se vuelve fórmula.
 */
export type Celda = string | number | Date | boolean | null | undefined;
export interface Columna { titulo: string; tipo?: 'texto' | 'numero' | 'dinero' | 'fecha' | 'fechahora'; ancho?: number }
export interface Hoja { nombre: string; columnas: Columna[]; filas: Celda[][] }

const ZONA = 'America/Mexico_City';
const enc = new TextEncoder();

// ── CRC-32 (lo exige el ZIP) ──
const TABLA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
function crc32(b: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < b.length; i++) c = TABLA_CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** ZIP sin comprimir: encabezado local + datos por archivo, directorio central y su cierre. */
function zip(archivos: { nombre: string; datos: Uint8Array }[]): Uint8Array {
  const partes: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const d = new Date();
  const hora = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
  const fecha = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  for (const a of archivos) {
    const nombre = enc.encode(a.nombre);
    const crc = crc32(a.datos);
    const loc = new DataView(new ArrayBuffer(30));
    loc.setUint32(0, 0x04034b50, true); loc.setUint16(4, 20, true); loc.setUint16(6, 0x0800, true); loc.setUint16(8, 0, true);
    loc.setUint16(10, hora, true); loc.setUint16(12, fecha, true); loc.setUint32(14, crc, true);
    loc.setUint32(18, a.datos.length, true); loc.setUint32(22, a.datos.length, true); loc.setUint16(26, nombre.length, true); loc.setUint16(28, 0, true);
    partes.push(new Uint8Array(loc.buffer), nombre, a.datos);
    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true); cen.setUint16(4, 20, true); cen.setUint16(6, 20, true); cen.setUint16(8, 0x0800, true); cen.setUint16(10, 0, true);
    cen.setUint16(12, hora, true); cen.setUint16(14, fecha, true); cen.setUint32(16, crc, true);
    cen.setUint32(20, a.datos.length, true); cen.setUint32(24, a.datos.length, true); cen.setUint16(28, nombre.length, true);
    cen.setUint32(42, offset, true);
    central.push(new Uint8Array(cen.buffer), nombre);
    offset += 30 + nombre.length + a.datos.length;
  }
  const tamCentral = central.reduce((n, x) => n + x.length, 0);
  const fin = new DataView(new ArrayBuffer(22));
  fin.setUint32(0, 0x06054b50, true); fin.setUint16(8, archivos.length, true); fin.setUint16(10, archivos.length, true);
  fin.setUint32(12, tamCentral, true); fin.setUint32(16, offset, true);
  const todo = [...partes, ...central, new Uint8Array(fin.buffer)];
  const out = new Uint8Array(todo.reduce((n, x) => n + x.length, 0));
  let o = 0;
  for (const x of todo) { out.set(x, o); o += x.length; }
  return out;
}

// ── Celdas ──
const esc = (s: string) => s
  // Caracteres que el XML no admite (vienen a veces pegados de WhatsApp): fuera.
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
function letra(i: number): string { let s = ''; let n = i + 1; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; }

/** Fecha → número de serie de Excel (días desde 1899-12-30), con la hora de pared de la clínica. */
function serie(v: Celda, conHora: boolean): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return Date.UTC(y, m - 1, d) / 864e5 + 25569;
  }
  const f = v instanceof Date ? v : new Date(String(v));
  if (isNaN(f.getTime())) return null;
  const p: Record<string, number> = {};
  for (const x of new Intl.DateTimeFormat('en-CA', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(f)) {
    if (x.type !== 'literal') p[x.type] = Number(x.value);
  }
  const dia = Date.UTC(p.year, p.month - 1, p.day) / 864e5 + 25569;
  return conHora ? dia + (p.hour * 3600 + p.minute * 60 + p.second) / 86400 : dia;
}

const ESTILO = { encabezado: 1, dinero: 2, fecha: 3, fechahora: 4, entero: 5 } as const;

function celda(ref: string, v: Celda, tipo: Columna['tipo']): string {
  if (v === null || v === undefined || v === '') return '';
  if (tipo === 'fecha' || tipo === 'fechahora') {
    const n = serie(v, tipo === 'fechahora');
    if (n !== null) return `<c r="${ref}" s="${ESTILO[tipo]}"><v>${Math.round(n * 864e5) / 864e5}</v></c>`;
  }
  if ((tipo === 'numero' || tipo === 'dinero') && typeof v !== 'boolean') {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) return `<c r="${ref}"${tipo === 'dinero' ? ` s="${ESTILO.dinero}"` : ''}><v>${n}</v></c>`;
  }
  const t = typeof v === 'boolean' ? (v ? 'Sí' : 'No') : v instanceof Date ? v.toISOString() : String(v);
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${esc(t.slice(0, 32000))}</t></is></c>`;
}

function hojaXml(h: Hoja): string {
  const cols = h.columnas.map((c, i) => {
    const ancho = c.ancho || (c.tipo === 'fechahora' ? 17 : c.tipo === 'fecha' ? 11.5 : c.tipo === 'dinero' ? 13 : c.tipo === 'numero' ? 9 : Math.min(45, Math.max(10, c.titulo.length + 2)));
    return `<col min="${i + 1}" max="${i + 1}" width="${ancho}" customWidth="1"/>`;
  }).join('');
  const filas: string[] = [];
  filas.push(`<row r="1">${h.columnas.map((c, i) => `<c r="${letra(i)}1" t="inlineStr" s="${ESTILO.encabezado}"><is><t>${esc(c.titulo)}</t></is></c>`).join('')}</row>`);
  h.filas.forEach((f, k) => {
    const r = k + 2;
    filas.push(`<row r="${r}">${h.columnas.map((c, i) => celda(`${letra(i)}${r}`, f[i], c.tipo)).join('')}</row>`);
  });
  const ultima = `${letra(Math.max(0, h.columnas.length - 1))}${h.filas.length + 1}`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + `<dimension ref="A1:${ultima}"/>`
    + `<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
    + `<sheetFormatPr defaultRowHeight="15"/>`
    + (cols ? `<cols>${cols}</cols>` : '')
    + `<sheetData>${filas.join('')}</sheetData>`
    + (h.columnas.length ? `<autoFilter ref="A1:${ultima}"/>` : '')
    + `</worksheet>`;
}

const nombreHoja = (s: string, i: number) => (s.replace(/[[\]:*?/\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 31) || `Hoja ${i + 1}`);

/** Los bytes del .xlsx. */
export function xlsxBytes(hojas: Hoja[]): Uint8Array {
  if (!hojas.length) throw new Error('Un libro de Excel necesita al menos una hoja.');
  const nombres: string[] = [];
  for (const [i, h] of hojas.entries()) {
    let n = nombreHoja(h.nombre, i); let k = 2;
    while (nombres.some((x) => x.toLowerCase() === n.toLowerCase())) n = `${nombreHoja(h.nombre, i).slice(0, 28)} ${k++}`;
    nombres.push(n);
  }
  const x = (s: string) => enc.encode(s);
  const cabeza = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  const archivos = [
    { nombre: '[Content_Types].xml', datos: x(cabeza + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
      + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
      + `<Default Extension="xml" ContentType="application/xml"/>`
      + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
      + `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>`
      + hojas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
      + `</Types>`) },
    { nombre: '_rels/.rels', datos: x(cabeza + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
      + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
      + `</Relationships>`) },
    { nombre: 'xl/workbook.xml', datos: x(cabeza + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
      + `<sheets>${nombres.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>`
      + (hojas.some((h) => h.columnas.length) ? `<definedNames>${hojas.map((h, i) => h.columnas.length
          ? `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">'${esc(nombres[i].replace(/'/g, "''"))}'!$A$1:$${letra(h.columnas.length - 1)}$${h.filas.length + 1}</definedName>` : '').join('')}</definedNames>` : '')
      + `</workbook>`) },
    { nombre: 'xl/_rels/workbook.xml.rels', datos: x(cabeza + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
      + hojas.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
      + `<Relationship Id="rId${hojas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
      + `</Relationships>`) },
    { nombre: 'xl/styles.xml', datos: x(cabeza + `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
      + `<numFmts count="3"><numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00"/><numFmt numFmtId="165" formatCode="yyyy-mm-dd"/><numFmt numFmtId="166" formatCode="yyyy-mm-dd hh:mm"/></numFmts>`
      + `<fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font><font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font></fonts>`
      + `<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>`
      + `<fill><patternFill patternType="solid"><fgColor rgb="FFF3EEE3"/><bgColor indexed="64"/></patternFill></fill></fills>`
      + `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>`
      + `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>`
      + `<cellXfs count="6">`
      + `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`
      + `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>`
      + `<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
      + `<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
      + `<xf numFmtId="166" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
      + `<xf numFmtId="1" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
      + `</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>`
      + `</styleSheet>`) },
    ...hojas.map((h, i) => ({ nombre: `xl/worksheets/sheet${i + 1}.xml`, datos: x(hojaXml(h)) })),
  ];
  return zip(archivos);
}

/** Baja el .xlsx con ese nombre (se le agrega la extensión si no la trae). */
export function descargarExcel(archivo: string, hojas: Hoja[]): void {
  const blob = new Blob([xlsxBytes(hojas).buffer as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = /\.xlsx$/i.test(archivo) ? archivo : `${archivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Un nombre de archivo decente: sin acentos ni signos, con guiones. */
export const nombreArchivo = (...partes: string[]) => partes.join(' ')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 80) || 'datos';
